<template>
  <fn-page-container>
    <fn-product-detail-reviews
      v-if="isReviewsOnly"
      :page="page"
      :product="product"
      :reviews-script="reviewsScript"
    />

    <RenderCacheable
      v-else-if="cacheKey"
      :cache-key="cacheKey"
    >
      <fn-product-detail
        v-if="breadcrumbs && page && product"
        :breadcrumbs="breadcrumbs"
        :page="page"
        :product="product"
        :reviews-script="reviewsScript"
        @change-variant="handleChangeVariant"
      />
    </RenderCacheable>
  </fn-page-container>
</template>

<script setup lang="ts">
import { AxiosError } from 'axios';

function handleChangeVariant({ url }: { url: string }) {
  return navigateTo({
    path: url,
    query: { preventScrollToTop: 'true' },
  });
}

definePageMeta({
  name: 'product-detail',
  routeCmsPage: () => 'p/product-detail',
});

const nuxtApp = useNuxtApp();
const mainStore = useMainStore();
const userStore = useUserStore();
const datalayerStore = useDatalayerStore();
const route = useRoute();
const reviewsScript = useReviewScript();

// Page is not immediately needed, so we start the request but await it further down just before we need it.
// This way, the request and all this processing can happen in parallel.
const pageRequest = mainStore.loadRoutePage(route);

const { $isocodes, $api, $log, $i18n } = nuxtApp;
const { query, params, path, fullPath } = route;
const { pdpBreadcrumbs } = mainStore;

// Define type down here to prevent access before $isocodes is defined
type IsoCodeKey = keyof typeof $isocodes;

const language = mainStore.language;
const languageIsoCode = $isocodes[language as IsoCodeKey];
const isReviewsOnly = query.isReviewsOnly === '1';
const isSearchPage = !!query.isSearchPage;
const isFilterPage = !!query.q;

const productId: string | undefined = (params.product as string)?.match(/(.+-)?(.*[^-])/)?.pop();

const { data: productRef, error: errorRef } = await useAsyncData(`product-detail-${productId}`, async () => {
  return (await $api.hybris.product.find(productId, language)).data;
});

let productUrl: string;
let product: Record<string, any>;
let breadcrumbs: Array<Record<string, any>>;
let page: Record<string, any>;

const cacheKey = computed(() =>
  generateCacheKeyPage(fullPath, page, {
    tealiumAudiences: userStore.tealiumAudiences,
    productId: !unref(errorRef) ? product.code : undefined,
  }),
);

const pageLogic = async () => {
  // Dereference to prevent leaking mutations into the response object of useAsyncData.
  // If we don't do this, any changes to the object will already be included in the data
  // sent from server -> client, and ultimately be repeated when this setup runs client-side.
  product = deepClone(unref(productRef));

  const error = unref(errorRef);
  if (!product || error) {
    $log.error('Failed to load product', {
      productId,
      error,
    });

    if ((error?.cause as AxiosError)?.status === 404) {
      // Remove leading and trailing slashes
      const redirectResponse = await useAsyncData(async () => (await $api.getRedirect(path)).data);
      const result = unref(redirectResponse.data);

      if (result?.redirectTo && result?.statusCode) {
        return navigateTo(result.redirectTo, { redirectCode: result.statusCode });
      }
    }

    return useCmsErrorPage();
  }

  // Prepend language to product URL from OCC response
  productUrl = mainStore.isMultilang && languageIsoCode ? `/${languageIsoCode}${product.url}` : product.url;

  // Redirect to full product url, if the current url does not match
  const isDifferentPage = ![productUrl, `${productUrl}/`].includes(path);
  if (productUrl && isDifferentPage && !isSearchPage && !isFilterPage) {
    $log.debug('Redirecting...', {
      from: path,
      to: productUrl,
      reason: 'Current URL does not match full SEO-relevant product URL',
    });

    return navigateToWithAdobeParam(productUrl, query, { redirectCode: 301 } as any);
  }

  // PLP stores its CMS page breadcrumbs so that we can include them in the PDP breadcrumbs.
  // This wouldn't make much sense for generic search pages, in which case we reset it again.
  if (isSearchPage || !Object.keys(product).length) {
    pdpBreadcrumbs.nodes = [];
  }

  // Deep copy array, we don't want to link reactive values between components
  breadcrumbs = pdpBreadcrumbs.nodes || product.breadCrumbs;
  breadcrumbs = breadcrumbs ? deepClone(breadcrumbs) : [];

  // Append PLP search url in breadcrumbs when coming from a PLP
  if (breadcrumbs?.length > 1 && pdpBreadcrumbs.nodes?.length) {
    let lastNonFilter = '';

    // If url only consists of ?q= query prepend last full url
    breadcrumbs.forEach((node: Record<string, any>, index: number) => {
      if (node.url?.startsWith('?q=')) {
        if (lastNonFilter === '') {
          lastNonFilter = breadcrumbs[index - 1]?.url ?? $i18n.t('url.search');
        }
        node.url = lastNonFilter + node.url;
      }
    });
  } else if (product.breadCrumbs && !pdpBreadcrumbs.nodes?.length) {
    breadcrumbs = product.breadCrumbs.filter((item: any) => item.id !== 'root' && item.title !== 'PLP');
  } else if (!breadcrumbs) {
    breadcrumbs = [];
  }

  // At last, add breadcrumb for the product itself
  breadcrumbs.push({
    id: product.code,
    title: product.brandName ? `${product.brandName} ${product.name}` : product.name,
    url: fullPath,
  });

  // Processing unrelated to page is done, now we need the page itself
  page = await pageRequest;

  datalayerStore.products = [product.analytics];

  if (page?.content) {
    // Set default description
    if (page.content.meta) {
      const description = $i18n.t('product.defaultDescription', {
        product: product.name,
      });

      const uspText = $i18n.t('pages.usp', {
        freeShippingThreshold: mainStore.getFreeShippingThreshold?.value,
      });

      page.content.meta.description = `${description} ${uspText}`;
    }

    // Add seo page content
    page.content.related_links = await useSeoRelatedCategories({
      relatedQuery: `brand:${product.brandCode}`,
      isBrandPage: !!page,
    });
  }

  await usePageMeta({ page, product, breadcrumbs });
};

await pageLogic();
</script>
